import { PayloadAction, createSlice } from "@reduxjs/toolkit";
import { ReduxState } from "../store";

/**
 * Per-tenant feature flags loaded at login time.
 * Keys must match the JSON property names returned by GET /api/v{version}/feature-flags.
 *
 * AI FEATURE GATE RULE (DEV_STANDARDS.md):
 * Use the useFeatureFlag() hook to read flags. Never read this state directly.
 * Always implement a non-AI fallback alongside any AI-gated feature.
 */
export interface FeatureFlagsState {
  /** Controls all runtime AI features for this tenant. Defaults to true (enabled). */
  isAiUseEnabled: boolean;
  /** True once flags have been loaded from the API. Prevents premature gating. */
  loaded: boolean;
}

const initialState: FeatureFlagsState = {
  isAiUseEnabled: true, // safe default — enabled until we know otherwise
  loaded: false,
};

export const featureFlagsSlice = createSlice({
  name: "featureFlags",
  initialState,
  reducers: {
    setFeatureFlags: (
      state,
      action: PayloadAction<Omit<FeatureFlagsState, "loaded">>,
    ) => {
      state.isAiUseEnabled = action.payload.isAiUseEnabled ?? true;
      state.loaded = true;
    },
    resetFeatureFlags: () => initialState,
  },
});

function createSelector<T>(
  fn: (d: ReduxState["persistedReducer"]["featureFlags"]) => T,
) {
  return ({ persistedReducer: { featureFlags } }: ReduxState) =>
    fn(featureFlags);
}

export const selectIsAiUseEnabled = createSelector((f) => f.isAiUseEnabled);
export const featureFlagsActions = featureFlagsSlice.actions;

export default featureFlagsSlice.reducer;
